import { useEffect, useMemo, useRef, useState } from "react";
import axiosInstance from "src/common/axiosInstance";
import TableCustomLoader from "src/components/Common/TableCustomLoader";
import { formatMetricName, applySelectAllMetrics, toggleMetricwithDependencies, clearAllMetrics, rawDimensions, isMetricDisabled, formatMetricString } from "src/helpers/commonHelper";
interface ExchangeMetricsState {
    id: number;
    name: string;
    iconUrl: string;
    display_metrics: string[];
    defaultMetrics: string[];
    selectedMetrics: string[];
}
type MetricPayload = {
    userId: number | string;
    exchanges: {
        exchangeId: number | string;
        selectedMetrics: string[];
    }[];
};

interface ExternalToggle {
    metricId: string;
    action: "select" | "deselect";
    timestamp: number;
    exchangeId?: number;
}

interface UserMetricsProps {
    userId: number | string;
    onMetricsChange?: (payload: MetricPayload) => void;
    interceptToggle?: (exchangeId: number, metricId: string, willBeSelected: boolean) => void;
    externalToggle?: ExternalToggle | null;
}
/* eslint-disable react/prop-types */
const UserMetrics: React.FC<UserMetricsProps> = ({ userId, onMetricsChange, interceptToggle, externalToggle }) => {
    const [metrics, setMetrics] = useState([]);
    const [selectedExchangeId, setSelectedExchangeId] = useState<number | null>(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [exchangeSelectionState, setExchangeSelectionState] = useState<ExchangeMetricsState[]>([]);
    const [allMetrics, setAllMetrics] = useState(false);
    const [loading, setLoading] = useState(true);
    const [showScrollButtons, setShowScrollButtons] = useState(false);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);


    const checkForOverflow = () => {
        const container = tabContainerRef.current;
        if (!container) return;

        const hasOverflow = container.scrollWidth > container.clientWidth;
        setShowScrollButtons(hasOverflow);

        if (hasOverflow) {
            setCanScrollLeft(container.scrollLeft > 0);
            setCanScrollRight(
                container.scrollLeft + container.clientWidth < container.scrollWidth
            );
        } else {
            setCanScrollLeft(false);
            setCanScrollRight(false);
        }
    };

    useEffect(() => {
        const container = tabContainerRef.current;
        if (!container) return;

        checkForOverflow();

        window.addEventListener("resize", checkForOverflow);
        container.addEventListener("scroll", checkForOverflow);

        return () => {
            window.removeEventListener("resize", checkForOverflow);
            container.removeEventListener("scroll", checkForOverflow);
        };
    }, [exchangeSelectionState]);



    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await axiosInstance.get(
                    `${process.env.REACT_APP_LARAVEL_API_URL}/campaign-exchange-details`,
                    {
                        params: {
                            userId: userId,
                            flag: true
                        },
                    }
                );
                const data = await response.data.data;
                setExchangeSelectionState(data);
                if (data.length > 0) {
                    setSelectedExchangeId(data[0].id);
                    setLoading(false);
                }
            } catch (error) {
                console.error('Error fetching data:', error);
                setLoading(false);
            }
        };

        fetchData();
    }, []);
    const currentExchange = exchangeSelectionState.find(ex => ex.id === selectedExchangeId);

    const getSortedMetrics = (exchange: ExchangeMetricsState) => {
        const { display_metrics, selectedMetrics, defaultMetrics } = exchange;

        return display_metrics
            .filter(metric => {
                const formattedName = formatMetricName(metric).toLowerCase();
                const searchTermLower = searchTerm.toLowerCase();

                // Split the formatted name into words
                const words = formattedName.split(' ');

                // Check if any word starts with the search term
                return words.some(word => word.startsWith(searchTermLower));
            })
            .sort((a, b) => {
                const isSelectedA = selectedMetrics.some(m => formatMetricString(m) === formatMetricString(a)) ? 1 : 0;
                const isSelectedB = selectedMetrics.some(m => formatMetricString(m) === formatMetricString(b)) ? 1 : 0;
                return isSelectedB - isSelectedA;
            });
    };

    const formattedDimensions = rawDimensions.map(dimension =>
        formatMetricString(dimension)
    );

    const dimensionSet = new Set(formattedDimensions);

    const dimensionMetrics = currentExchange
        ? getSortedMetrics(currentExchange).filter(metric =>
            dimensionSet.has(formatMetricString(metric))
        )
        : [];

    const remainingMetrics = currentExchange
        ? getSortedMetrics(currentExchange).filter(metric =>
            !dimensionSet.has(formatMetricString(metric))
        )
        : [];

    // Inside UserMetrics component
    const updateMetricsPayload = () => {
        const payload: MetricPayload = {
            userId, // from props
            exchanges: exchangeSelectionState.map(exchange => ({
                exchangeId: exchange.id,
                selectedMetrics: exchange.selectedMetrics,
            })),
        };
        if (onMetricsChange) {
            onMetricsChange(payload);
        }
    };

    useEffect(() => {
        updateMetricsPayload();
    }, [exchangeSelectionState]);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
    };

    const handleMetricClick = (exchangeId: number, metric: string) => {
        if (interceptToggle) {
            const exchange = exchangeSelectionState.find(ex => ex.id === exchangeId);
            if (!exchange) return;
            const isCurrentlySelected = exchange.selectedMetrics.some(
                m => formatMetricString(m) === formatMetricString(metric)
            );
            interceptToggle(exchangeId, metric, !isCurrentlySelected);
        } else {
            toggleMetricwithDependencies(exchangeId, metric, setExchangeSelectionState);
        }
    };

    useEffect(() => {
        if (!externalToggle) return;
        const { metricId, action, exchangeId } = externalToggle;
        const normalizedMetric = formatMetricString(metricId);

        // Collect exchange IDs that need toggling
        const exchangeIdsToToggle: number[] = [];
        for (const ex of exchangeSelectionState) {
            if (exchangeId !== undefined && ex.id !== exchangeId) continue;
            const hasMetric = ex.display_metrics.some(
                dm => formatMetricString(dm) === normalizedMetric
            );
            if (!hasMetric) continue;
            const isSelected = ex.selectedMetrics.some(
                m => formatMetricString(m) === normalizedMetric
            );
            const shouldToggle = (action === "select" && !isSelected) || (action === "deselect" && isSelected);
            if (shouldToggle) {
                exchangeIdsToToggle.push(ex.id);
            }
        }

        // Call toggleMetricwithDependencies for each — React 18 batches these
        for (const exId of exchangeIdsToToggle) {
            toggleMetricwithDependencies(exId, metricId, setExchangeSelectionState);
        }
    }, [externalToggle]);

    const tabContainerRef = useRef<HTMLDivElement>(null);
    // Scroll tabs horizontally
    const scrollTabsByOne = (direction: "left" | "right") => {
        const container = tabContainerRef.current;
        if (!container) return;

        const tab = container.querySelector(".tab");
        if (!tab) return;

        const tabWidth = (tab as HTMLElement).offsetWidth;
        const scrollAmount = direction === "right" ? tabWidth : -tabWidth;

        container.scrollBy({ left: scrollAmount, behavior: "smooth" });
    };

    const scrollLeft = () => scrollTabsByOne("left");
    const scrollRight = () => scrollTabsByOne("right");

    return (
        <>
            <style>
                {`
                    /* Optional: also remove default box-shadow and background */
                    .accordion-button {
                        background-color: transparent !important;
                        box-shadow: none !important;
                    }
                `}
            </style>
            {loading ? (
                <TableCustomLoader length={2}
                    message="Almost ready! Preparing metrics data." />
            ) : (
                <>
                    <div className="tab-scroll-wrapper">
                        {showScrollButtons && (
                            <span
                                className={`scroll-btn ${!canScrollLeft ? "disabled" : ""}`}
                                onClick={canScrollLeft ? scrollLeft : undefined}
                            >
                                &laquo;
                            </span>
                        )}
                        <div className="tab-scroll-container" ref={tabContainerRef}>
                            {exchangeSelectionState.map((exchange, index) => (
                                <div
                                    key={exchange.id}
                                    className={`tab ${selectedExchangeId === exchange.id ? "tab custom-tab" : "tab"}`}
                                    onClick={() => { setSelectedExchangeId(exchange.id); }}
                                >
                                    {exchange.name}
                                    <span className="selected-count">
                                        {exchange.selectedMetrics?.length || 0}
                                    </span>
                                </div>
                            ))}
                        </div>
                        {showScrollButtons && (
                            <span
                                className={`scroll-btn ${!canScrollRight ? "disabled" : ""}`}
                                onClick={canScrollRight ? scrollRight : undefined}
                            >
                                &raquo;
                            </span>
                        )}
                    </div>
                    {currentExchange && (
                        <>
                            <div className="platform-name-header">
                                <div className="platform-name">
                                    {currentExchange?.iconUrl && (
                                        <div className="custom-icon-box">
                                            <img
                                                src={currentExchange.iconUrl}
                                                alt={`${currentExchange.name} icon`}
                                                onError={(e) => {
                                                    (e.target as HTMLImageElement).style.display = 'none';
                                                }}
                                                width="auto"
                                                height="17px"
                                            />
                                        </div>
                                    )}
                                    <h3>{currentExchange.name}</h3>
                                </div>
                                <div className="header-right-div">
                                    <input
                                        type="text"
                                        placeholder="Search"
                                        value={searchTerm}
                                        onChange={handleSearchChange}
                                        className="column-search-input form-control"
                                    />
                                    <div className="header-btn-grp">
                                        <button
                                            type="button"
                                            style={{ whiteSpace: "nowrap" }}
                                            className="btn btn-outline-secondary button-secondary"
                                            onClick={() =>
                                                applySelectAllMetrics(true, selectedExchangeId, setExchangeSelectionState, setAllMetrics)
                                            }
                                        >
                                            Select All
                                        </button>
                                        <button
                                            style={{ whiteSpace: "nowrap" }}
                                            type="button"
                                            className="btn btn-outline-secondary button-secondary"
                                            onClick={() => clearAllMetrics(selectedExchangeId, setExchangeSelectionState, setAllMetrics)}
                                        >
                                            Clear All
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="metrics-container custom-scrollbar">
                                <div >
                                    <h6>Dimensions</h6>
                                    <div className="user-metrics-section">
                                        {dimensionMetrics.map((metric, index) => {
                                            const isSelected = currentExchange.selectedMetrics.some(
                                                m => formatMetricString(m) === formatMetricString(metric)
                                            );
                                            return (
                                                <div
                                                    className={`metric-tile custom-tile ${isSelected ? 'selected' : ''}`}
                                                    key={index}
                                                    onClick={(e) => {
                                                        // Only handle clicks directly on the tile background
                                                        if (e.target === e.currentTarget) {
                                                            handleMetricClick(currentExchange.id, metric);
                                                        }
                                                    }}
                                                >
                                                    <div className="metric-control-wrapper">
                                                        <input
                                                            id={`metric-checkbox-${index}`}
                                                            className="form-check-input checkbox-input"
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            onChange={() => handleMetricClick(currentExchange.id, metric)}
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                        <label
                                                            htmlFor={`metric-checkbox-${index}`}
                                                            className="checkbox-label custom-label"
                                                            onClick={(e) => {
                                                                e.preventDefault(); // Prevent default label behavior
                                                                handleMetricClick(currentExchange.id, metric);
                                                            }}
                                                        >
                                                            <span className="metric-name">
                                                                {formatMetricName(metric)}
                                                            </span>
                                                        </label>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                                <div >
                                    <h6>Metrics</h6>
                                    <div className="user-metrics-section">
                                        {remainingMetrics.map((metric, index) => {
                                            const isSelected = currentExchange.selectedMetrics.some(
                                                m => formatMetricString(m) === formatMetricString(metric)
                                            );
                                            const disabled = isMetricDisabled(formatMetricString(metric), currentExchange.selectedMetrics);
                                            return (
                                                <div
                                                    className={`metric-tile custom-tile ${isSelected ? 'selected' : ''}`}
                                                    key={index}
                                                    onClick={(e) => {
                                                        // Only handle clicks directly on the tile background
                                                        if (e.target === e.currentTarget && !disabled) {
                                                            handleMetricClick(currentExchange.id, metric);
                                                        }
                                                    }}
                                                >
                                                    <div className="metric-control-wrapper">
                                                        <input
                                                            id={`metric-checkbox-${index}`}
                                                            className="form-check-input checkbox-input"
                                                            type="checkbox"
                                                            checked={isSelected && !disabled}
                                                            onChange={() => handleMetricClick(currentExchange.id, metric)}
                                                            onClick={(e) => e.stopPropagation()}
                                                            disabled={disabled}
                                                        />
                                                        <label
                                                            htmlFor={`metric-checkbox-${index}`}
                                                            className="checkbox-label custom-label"
                                                            onClick={(e) => {
                                                                e.preventDefault(); // prevent default label behavior
                                                                if (!disabled) {
                                                                    handleMetricClick(currentExchange.id, metric);
                                                                }
                                                            }}
                                                        >
                                                            <span className="metric-name">
                                                                {formatMetricName(metric)}
                                                            </span>
                                                        </label>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </>
            )}
        </>
    )
}

export default UserMetrics;
